/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Button } from '@connected/ui/components/button';
import { Popout, PopoutBody, PopoutContent, PopoutFooter } from '@connected/ui/components/popout';
import { ScrollArea } from '@connected/ui/components/scroll-area';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useScreenSize } from '@connected/ui/hooks/use-screen-size';
import { Container } from '@connected/ui/layouts/container';
import { cn } from '@connected/ui/lib/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronDown, CloudDownload, CloudOff, Share2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DataTable } from '@/components/data-table';
import { Loading } from '@/components/loading';
import { organizationIdOptional } from '@/config/web-validators';

export const Route = createFileRoute('/(auth)/projects/$projectVersionId/documents/')({
    component: Page,
    validateSearch: organizationIdOptional,
    pendingComponent: () => <Loading action="Loading projects" />,
});

function Page() {
    return (
        <Container>
            <Container.Header>
                {/* TODO: translate */}
                <Container.Title>Documenten</Container.Title>
            </Container.Header>
            <Container.Content>
                <Form />
            </Container.Content>
        </Container>
    );
}

// Add a stable versionNumber so it doesn't depend on the table row index
type DocumentVersion = components['schemas']['ListElectricalProjectDocumentQueryResultVersion'] & {
    type: 'version';
    isExpanded?: boolean;
    toggleExpanded: () => void;
    versionNumber: number;
};

type DocumentData = components['schemas']['ListElectricalProjectDocumentQueryResultDocument'] & {
    type: 'data';
};

function Form() {
    const search = Route.useSearch();
    const params = Route.useParams();

    const documentsQuery = useQuery({
        ...elektraAppQueryOptions.organisation.projects.documentListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }),
        refetchInterval: 5000,
    });

    const [data, setData] = useState<[DocumentVersion, DocumentData[]][]>([]);

    const columns = useMemo<ColumnDef<(DocumentVersion | DocumentData)>[]>(() => ([
        {
            id: 'dropdownChevron',
            cell: ({ row }) => {
                if (row.original.type !== 'version') {
                    return null;
                }

                return (
                    <Button variant="ghost" size="icon" className="w-fit h-fit justify-start p-2" onClick={() => (row.original as DocumentVersion).toggleExpanded()}>
                        <ChevronDown className="w-4 h-4" />
                    </Button>
                );
            },
        },
        {
            id: 'name',
            header: 'Naam',
            cell: ({ row, table }) => {
                if (row.original.type !== 'version') {
                    return row.original.FileName;
                }

                // Count only version rows up to this point to get the correct version number
                const versionRowIndex = table.getRowModel().rows.slice(0, row.index + 1).filter(r => r.original.type === 'version').length - 1;

                return `Versie ${documentsQuery.data?.Count ? documentsQuery.data.Count - versionRowIndex : versionRowIndex + 1}`;
            },
        },
        {
            id: 'type',
            header: 'Type',
            cell: ({ row }) => {
                if (row.original.type !== 'version') {
                    switch (row.original.ContentKind) {
                        case 'ElectricalProjectInspectionPlan':
                            return 'Inspectieplan';
                        case 'ElectricalProjectPowerShutdownPlan':
                            return 'Schakelbevoegdheid';
                        case 'ElectricalProjectPriceQuote':
                            return 'Offerte';
                        case 'ElectricalProjectNen1010Report':
                            return 'NEN 1010 rapport';
                        case 'ElectricalProjectNen3140Report':
                            return 'NEN 3140 rapport';
                        case 'ElectricalProjectScope8Report':
                            return 'Scope 8 rapport';
                        case 'ElectricalProjectScope10Report':
                            return 'Scope 10 rapport';
                        case 'ElectricalProjectScope12Report':
                            return 'Scope 12 rapport';
                        case 'ElectricalProjectSevereDefectNotification':
                            return 'Serieus defect melding';
                        default:
                            return 'Document';
                    }
                }

                return null;
            },
        },
        {
            id: 'sharedStatus',
            header: 'Status',
            cell: ({ row }) => {
                if (row.original.type !== 'version') {
                    switch (row.original.Status) {
                        case 'Completed':
                            return 'Voltooid';
                        case 'Failed':
                            return 'Mislukt';
                        case 'Scheduled':
                            return 'Gepland';
                        case 'Unscheduled':
                            return 'Niet gepland';
                        default:
                            return 'Document';
                    }
                }

                return row.original.DocumentStatus === 'Published' ? 'Gedeeld' : 'Niet gedeeld';
            },
        },
        {
            id: 'responsibleInspector',
            header: 'Inspecteurs',
            cell: ({ row }) => row.original.type === 'version' ? row.original.CreateUserDisplayName : null,
        },
        {
            id: 'createdAtDate',
            header: 'Datum',
            cell: ({ row }) => {
                if (row.original.type !== 'version') {
                    return null;
                }

                if (!row.original.CreateDate) {
                    return 'Geen datum';
                }

                return new Date(row.original.CreateDate).toLocaleDateString('nl-NL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            },
        },
        {
            id: 'actions',
            cell: ({ row }) => {
                if (row.original.type !== 'version') {
                    return (
                        <>
                            <DownloadButton documentId={row.original.DocumentId} fileName={row.original.FileName} />
                        </>
                    );
                }

                return (
                    <>
                        <PublishButton electricalProjectVersionId={row.original.ElectricalProjectVersionId} disabled={row.original.DocumentStatus === 'Published'} />
                    </>
                );
            },
        },
    ]), [documentsQuery.data?.Count]);

    const toggleExpanded = useCallback((version: DocumentVersion) => {
        setData(state => state.map(item => item[0].ElectricalProjectVersionId === version.ElectricalProjectVersionId && item[0].type === 'version'
            ? [
                    {
                        ...item[0],
                        isExpanded: !item[0].isExpanded,
                    },
                    item[1],
                ] as [DocumentVersion, DocumentData[]]
            : item));
    }, []);

    useEffect(() => {
        if (!documentsQuery.data?.Versions) {
            setData(Array.from({ length: 10 }, () => ([{} as DocumentVersion, [] as DocumentData[]])));
            return;
        }

        setData(documentsQuery.data.Versions.map(({ Documents, ...rest }, index, arr) => [
            {
                type: 'version' as const,
                isExpanded: false,
                versionNumber: arr.length - index,
                toggleExpanded: () => toggleExpanded({ type: 'version', isExpanded: false, toggleExpanded: () => { }, versionNumber: arr.length - index, ...rest }),
                ...rest,
            },
            Documents?.map(document => ({
                type: 'data' as const,
                ...document,
            })) ?? [],
        ]));
    }, [documentsQuery.data?.Versions, toggleExpanded]);

    const tableColumns = useMemo<ColumnDef<DocumentVersion | DocumentData>[]>(() => documentsQuery.isLoading
        ? columns.map(column => (
                {
                    ...column,
                    cell: () => <Skeleton className="h-8 w-full" />,
                }
            ))
        : columns, [columns, documentsQuery.isLoading]);

    const tableData = useMemo(() => {
        return data.map(([version, documents]) => {
            if (!version.isExpanded) {
                return [version];
            }

            return [version, ...documents];
        }).flat();
    }, [data]);

    return (
        <form
            onSubmit={event => event.preventDefault()}
            className="flex flex-col gap-4 p-6"
        >
            <DataTable<DocumentVersion | DocumentData, unknown>
                columns={tableColumns}
                data={tableData}
                className="min-w-[915px]"
                cellClassName={cell => cn('', {
                    'p-2': cell.column.id === 'DropdownChevron',
                    'pl-0': cell.column.id === 'Name' || cell.column.id === 'IconButtons',
                    'w-[20rem]': cell.column.id === 'Name',
                    'w-[1rem]': cell.column.id === 'IconButtons',
                    'bg-background-secondary': !cell.row.original,
                    'bg-muted/50': cell.row.original?.type !== 'version',
                })}
            />
            <div className="flex flex-col justify-between items-center gap-2 sm:flex-row">
                <Button className="w-fit" asChild variant="ghost">
                    <Link
                        // if no organization documents page was accessed from the summary finalization
                        to={search.organizationId ? '/projects' : '/projects/$projectVersionId/re-inspections/summary'}
                        params={{
                            projectVersionId: params.projectVersionId,
                        }}
                    >
                        <span>Ga terug</span>
                    </Link>
                </Button>
            </div>
        </form>
    );
}

function PublishButton({ electricalProjectVersionId, disabled = false }: { electricalProjectVersionId: string | null | undefined; disabled?: boolean }) {
    const queryClient = useQueryClient();
    const params = Route.useParams();
    const [open, setOpen] = useState(false);
    const { isMobile } = useScreenSize();

    const handleOpenChange = (isOpen: boolean) => {
        setOpen(isOpen);
    };

    const documentsQuery = useQuery(elektraAppQueryOptions.project.publishProjectOptions({
        electricalProjectVersionId: electricalProjectVersionId!,
    }));

    const mutation = api.useMutation('post', '/api/v2/electrical/projects/publish', {
        onSettled: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.organisation.projects.documentListOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
        },
    });

    return (
        <Popout open={open} onOpenChange={handleOpenChange}>
            <Button
                variant="ghost"
                size="icon"
                className="w-fit h-fit justify-start p-2"
                disabled={disabled || !electricalProjectVersionId || mutation.isPending}
                onClick={() => handleOpenChange(true)}
            >
                <Share2 className="w-4 h-4" />
            </Button>
            <PopoutContent
                aria-describedby="observation-content-description"
                className={cn('max-w-xl', {
                    'max-w-none': isMobile,
                })}
            >
                <PopoutBody className="gap-6 py-6">
                    <div className="flex flex-col gap-2">
                        <h1 className="text-lg font-semibold text-text-primary">Delen met klantportaal</h1>
                        <h2 className="text-sm font-normal text-text-tertiary">Je staat op het punt om nieuwe documenten van de inspectie te delen in de klantomgeving. De hieronder vermelde contactpersonen ontvangen automatisch een notificatie zodra de documenten zijn gepubliceerd.</h2>
                    </div>
                    <ScrollArea className="h-96">
                        <div className="flex flex-col gap-5">
                            {documentsQuery.isLoading
                                ? (
                                        <>
                                            {
                                                Array.from({ length: 6 }).map(_ => (
                                                    <Skeleton className="h-20 w-full" key={crypto.randomUUID()} />
                                                ))
                                            }
                                        </>
                                    )
                                : (
                                        <>
                                            {
                                                documentsQuery.data?.Organizations?.map(organization => (
                                                    <OrganizationRecipient key={organization.ElectricalProjectOrganizationId} organization={organization} />
                                                ))
                                            }
                                            {
                                                documentsQuery.data?.Organizations?.length === 0 && (
                                                    <p className="text-sm font-normal text-text-tertiary">Er zijn geen contactpersonen gevonden voor dit project.</p>
                                                )
                                            }
                                        </>
                                    )}
                        </div>
                    </ScrollArea>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        variant="outline"
                        onClick={() => {
                            handleOpenChange(false);
                        }}
                    >
                        Terug
                    </Button>
                    <Button
                        className="w-full"
                        onClick={async () => {
                            await await mutation.mutateAsync({
                                body: {
                                    ElectricalProjectVersionId: electricalProjectVersionId ?? '',
                                },
                            });
                            handleOpenChange(false);
                        }}
                        disabled={disabled || !electricalProjectVersionId || mutation.isPending || documentsQuery.isLoading}
                    >
                        Documenten delen
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

function OrganizationRecipient({
    organization,
}: {
    organization: components['schemas']['PublishElectricalProjectQueryResultOrganization'];
}) {
    return (
        <div className="rounded-xl bg-background-tertiary p-6 gap-4 flex flex-col">
            <div className="flex flex-col gap-1">
                <h1 className="font-semibold text-base">{organization?.Name}</h1>
                <h4 className="font-normal text-sm text-text-tertiary">
                    {[
                        organization.IsObjectAdministrator && 'Gebouwbeheerder',
                        organization.IsObjectOwner && 'Gebouweigenaar',
                        organization.IsObjectTenant && 'Gebouwgebruiker',
                        organization.IsProjectCustomer && 'Gebouwopdrachtgever',
                    ].filter(Boolean).join(', ')}
                </h4>
            </div>
            <div className="flex flex-col gap-3">
                {organization?.ContactPersons?.map(contactPerson => (
                    <div key={contactPerson.ElectricalProjectContactPersonId} className="flex flex-col sm:flex-row justify-between sm:items-center flex-wrap ">
                        <div className="flex flex-col">
                            <h2 className="text-sm font-semibold text-text-primary">
                                {
                                    [
                                        ...[contactPerson.FirstName, contactPerson.MiddleName, contactPerson.LastName].join(' '),
                                    ]
                                }
                            </h2>
                            <h4 className="text-sm font-normal text-text-tertiary">{contactPerson?.Email}</h4>
                        </div>
                        <p className="text-xs font-medium text-text-tertiary">{contactPerson?.JobTitleDescription}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function DownloadButton({ documentId, disabled = false, fileName, fileExtension }: { documentId: string | null | undefined; disabled?: boolean; fileName?: string | null | undefined; fileExtension?: string | null | undefined }) {
    const query = api.useQuery('get', '/api/v2/documents/download', {
        params: {
            query: {
                DocumentId: documentId ?? '',
            },
        },
        parseAs: 'blob',
    });

    const url = useMemo(() => {
        if (!documentId || !query.data) {
            return null;
        }
        const extension = fileExtension ?? fileName?.split('.').pop();
        const blob = new Blob([query.data], { type: `application/${extension}` });
        const url = URL.createObjectURL(blob);
        return url;
    }, [documentId, query.data, fileExtension, fileName]);

    return (

        <Button
            variant="ghost"
            size="icon"
            className="w-fit h-fit justify-start p-2"
            disabled={disabled || !documentId || !url}
            asChild={!!url}
        >
            {url
                ? <a href={url} download={fileName}><CloudDownload className="w-4 h-4" /></a>
                : <CloudOff className="w-4 h-4" />}
        </Button>
    );
}
